import { atom } from 'jotai';
import { StaticRoute } from 'nextjs-routes';
import QueryString from 'qs';

import { reservePaymentAPI } from 'src/stores/payments/apis';
import { UTMParamsAtom } from 'src/stores/utm/atoms';
import { WindowMessageType } from 'src/types/message';
import {
  getErrorCode,
  PaymentPopupInfo,
  PgPaymentMethod,
  PgPaymentMethodType,
} from 'src/types/Payments';
import getDeviceInfo from 'src/utils/device/info';
import { openPopupWithInitData, sendMessageToPopupWithRetry } from 'src/utils/popup';

export const paymentPath = atom((get) => {
  const UTMParams = get(UTMParamsAtom);
  const UTMParamsStringified = QueryString.stringify(UTMParams, {
    addQueryPrefix: true,
  });
  const url = '/payment/' + UTMParamsStringified;
  return url as StaticRoute<'/payment'>['pathname'];
});

const paymentPopupAtom = atom<WindowProxy | null>(null);

export const closePaymentPopupAtom = atom(null, (get, set) => {
  const paymentPopup = get(paymentPopupAtom);
  if (paymentPopup && !paymentPopup.closed) {
    paymentPopup.close();
  }
  set(paymentPopupAtom, null);
});

export const showPaymentPopupAtom = atom(
  null,
  (
    get,
    set,
    {
      paymentPopupInfo,
      pgPaymentMethod,
    }: { paymentPopupInfo: PaymentPopupInfo; pgPaymentMethod?: PgPaymentMethodType }
  ) => {
    // 새 팝업을 열기 전 기존 결제 팝업을 닫아 orphan 팝업 방지 (랜덤 창 이름 사용 시 다중 팝업 대응)
    set(closePaymentPopupAtom);

    const url = get(paymentPath);

    const { isMobile } = getDeviceInfo();

    let popupSizeOption: string | undefined;

    // 모바일에서는 viewport 기준으로 팝업 크기 자동 조정
    if (!isMobile) {
      const popupWidth = 650;
      const popupHeight = 650;
      const screenLeft = window.screenLeft;
      const screenTop = window.screenTop;

      const left = (screenLeft >= 0 ? 1 : -1) * (screen.width / 2) - popupWidth / 2;
      const top = (screenTop >= 0 ? 1 : -1) * (screen.height / 2) - popupHeight / 2;

      popupSizeOption = `width=${popupWidth},height=${popupHeight},left=${left},top=${top}`;
    }

    const popup = openPopupWithInitData(
      url,
      {
        type: WindowMessageType.PaymentPopupInfo,
        paymentPopupInfo,
      },
      popupSizeOption
    );

    set(paymentPopupAtom, popup);

    // Adyen만 parallel reserve 적용 — Toss/Stripe는 유저가 결제 버튼 클릭 시 직접 reserve 호출
    if (!popup || pgPaymentMethod !== PgPaymentMethod.ADYEN) return;

    reservePaymentAPI(paymentPopupInfo.product, pgPaymentMethod, get(UTMParamsAtom))
      .then((reservation) => {
        if (popup.closed) return;
        sendMessageToPopupWithRetry(popup, {
          type: WindowMessageType.PaymentsReservation,
          reservation,
        });
      })
      .catch((e) => {
        if (popup.closed) return;
        sendMessageToPopupWithRetry(popup, {
          type: WindowMessageType.PaymentsReservation,
          reservation: null,
          errorCode: getErrorCode(e.response?.data?.code),
        });
      });
  }
);

/**
 * 스타터패키지 넛징용 UI가 노출되었는지 여부 (과도한 넛징 노출 방지)
 */
export const isStarterPackageBannerShownAtom = atom(false);
